import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork, SessionInformationService, UtilityService } from 'app/core/core.module';
import { Customer, CustomerType, Load, Product, ProductCategory, TicketAttachment } from 'app/core/model/entity-model';
import { InputDialogComponent, SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm, custom } from 'devextreme/ui/dialog';
import { environment } from 'environments/environment';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ManualLoadDialogComponent } from '../manual-load-dialog/manual-load-dialog.component';
import { LoadService } from 'app/core/load.service';
import { Utility } from 'app/core/utility';
import { QuoteService } from 'app/core/quote.service';
import { QuoteDetailComponent } from '../quote-detail/quote-detail.component';
import { LoadAdditiveOverviewDialogComponent } from '../load-additive-overview-dialog/load-additive-overview-dialog.component';

@Component({
    standalone: false,
    selector: 'app-load-overview',
    templateUrl: './load-overview.component.html',
    styleUrls: ['./load-overview.component.scss'],
})
export class LoadOverviewComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    router = inject(Router);
    sessionInfo = inject(SessionInformationService);
    appInfo = inject(AppInformationService);
    auth = inject(AuthService);
    modalService = inject(BsModalService);
    utility = inject(UtilityService);
    loadService = inject(LoadService);
    quoteService = inject(QuoteService);

    attachments: TicketAttachment[] = [];
    uploadHeader: any;
    uploadUrl = (environment.serviceName + '/file/SaveTicketAttachment') as string;
    loads: Load[];

    filter: any = {};
    customerTypes: CustomerType[];
    isAdmin = false;
    salesPersons: any[];
    private refreshTimeout: any;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        this.isAdmin = this.auth.hasRole('Loads:Admin');
        clearInterval(this.refreshTimeout);

        this.appInfo.pleaseWaitShow();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async getLoads() {
        const loads = await this.uow.customers.getLoads('customer, ticketAttachment, loadAdditives, product');
        this.loadService.setAdditivesHtml(loads);
        this.loads = loads;
    }

    async getData(take?: number) {
        this.appInfo.pleaseWaitShow();
        await this.getLoads();
        await this.getAttachments();
        this.appInfo.pleaseWaitHide();
        clearInterval(this.refreshTimeout);
    }

    onRowPrepared(e) {
        if (e.rowType !== 'data' || !e.data) return;

        const load = e.data as Load;
        if (load.onHold) e.rowElement.className += ' on-hold';
        if (!load.ticketAttachment && load.manaulHasDelivery) e.rowElement.className += ' manual-delivery';
    }

    onCellPrepared(e) {
        if (e.rowType !== 'data') return;
        const load = e.data as Load;

        if (!load.productId) {
            if (e.column.dataField === 'productId') {
                e.cellElement.className = e.cellElement.className + ' red-cell';
            }
        }
        if (!load.customerId) {
            if (e.column.dataField === 'customer.companyName') {
                e.cellElement.className = e.cellElement.className + ' red-cell';
            }
        }
        if (!load.product.deliveryProductId && load.carrier === 'BREWERS') {
            if (e.column.dataField === 'carrier') {
                e.cellElement.className = e.cellElement.className + ' red-cell';
            }
        }
        if (e.column.dataField === 'price') {
            if (!load.priceVerified && (load.ticketAttachment?.typeId === TicketAttachment.Types.RediMix || !load.ticketAttachment)) {
                e.cellElement.className = e.cellElement.className + ' price-not-verified';
            } else if (load.quotedPrice) {
                e.cellElement.className = e.cellElement.className + ' price-from-quote';
            }
        }
    }

    customizeColumns(columns) {
        columns.forEach((column) => {
            if (column.dataField !== 'price') {
                // console.log('here');
                column.allowEditing = false;
            }
        });
    }

    async onRowUpdated(e) {
        // console.log('asdfasdf', e);
        if (e.data) {
            await this.priceVerify(e.data);
            // if (e.data.price != 0) e.data.priceVerified = new Date();
            // else e.data.priceVerified = null;
        }
        await this.uow.commit();
    }

    rowDoubleClick(e: any) {
        // this.open(e.data);
    }

    async cellDoubleClick(e: any) {
        if (e.column.dataField === 'priceVerified') {
            this.appInfo.pleaseWaitShow();
            await this.priceVerify(e.data);
            await this.uow.commit();
            this.appInfo.pleaseWaitHide();
        } else if (e.column.dataField !== 'price') {
            this.edit(e.data);
        }
    }

    async priceVerify(load: Load) {
        if (load.price != 0 && !load.priceVerified) {
            load.priceVerified = new Date();
            await this.addCustomerMixStrength(load);
        } else load.priceVerified = null;
    }

    async addCustomerMixStrength(load: Load) {
        if (!load.customer || !load.product || load.product.categoryId !== ProductCategory.Types.RMix || !load.product.mixStrengthId) return;
        const customerMixStrength = (await this.uow.getCustomerMixStrengthLinks(load.customerId, null, load.product.mixStrengthId))[0];
        if (customerMixStrength) return;
        await this.uow.commit([
            this.uow.customerMixStrengthLinks.createEntity({
                customerId: load.customerId,
                mixStrengthId: load.product.mixStrengthId,
                price: load.price,
            }),
        ]);
    }

    clear() {
        this.filter = {};
    }

    openCustomer(customerId?: any) {
        this.router.navigate([`/customers/${customerId ? customerId : 'new'}`]);
    }

    async edit(load?: Load) {
        if (load && load.ticketAttachment) {
            await alert('Only manual loads can be edited', this.appInfo.settings.company);
            return;
        }

        let model: any;
        if (load) {
            model = {
                customerId: load.customerId,
                customerString: load.customerString,
                productId: load.productId,
                productString: load.productString,
                job: load.description1,
                truck: load.vehicle,
                ticketNumber: load.ticketNumber,
                loadCount: 1,
                weightPerLoad: load.weightQuantity,
                price: load.price,
                loadDate: load.loadDate,
                description: load.description2,
                hasDelivery: load.manaulHasDelivery,
                deliveryProductId: load.deliveryProductId,
                deliveryPrice: load.deliveryPrice,
                startTime: load.startTime,
                endTime: load.endTime,
            };
        }
        const modalRef = this.modalService.show(ManualLoadDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                model: model,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result?.success && result.value) {
                this.appInfo.pleaseWaitShow();
                const model = result.value;

                if (!load) {
                    load = this.uow.loads.createEntity({
                        created: new Date(),
                    }) as Load;
                }

                load.loadDate = moment(model.loadDate).startOf('day').toDate();
                load.time = Utility.toTimeOnly(model.loadDate);
                load.customerId = model.customerId;
                load.customerString = model.customerString;
                load.productId = model.productId;
                load.productString = model.productString;
                load.description1 = model.job;
                load.description2 = model.description;
                load.vehicle = model.truck;
                load.ticketNumber = model.ticketNumber;
                load.weightQuantity = model.weightPerLoad * model.loadCount;
                if (load.price !== model.price) load.priceVerified = null;
                load.price = model.price;
                load.manaulHasDelivery = model.hasDelivery;
                load.deliveryProductId = model.deliveryProductId;
                load.deliveryPrice = model.deliveryPrice;
                load.startTime = model.isHourly ? model.startTime : null;
                load.endTime = model.isHourly ? model.endTime : null;

                try {
                    await this.uow.commit();
                    await this.getData();
                } catch (error: any) {
                    await alert(error?.message ?? 'An error occurred while attempting to save the load', this.appInfo.settings.company);
                    this.appInfo.pleaseWaitHide();
                    return;
                }
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async createInvoices() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const sameCustomer = _.every(selectedRows, (x) => x.customerId === selectedRows[0].customerId);
        if (!sameCustomer) {
            alert(`Customer not same for all selected lines.`, this.appInfo.appTitle);
            return;
        }

        const priceVerified = _.every(selectedRows, (x) => x.priceVerified || x.ticketAttachment?.typeId === TicketAttachment.Types.Scale);
        if (!priceVerified) {
            alert(`Price not verified for all selected lines.`, this.appInfo.appTitle);
            return;
        }

        const deliveryProductCheck = _.every(selectedRows, (x) => x.carrier !== 'BREWERS' || !!x.product.deliveryProductId);
        if (!deliveryProductCheck) {
            alert(`Delivery product not set for one or more products.`, this.appInfo.appTitle);
            return;
        }

        const result = await confirm(`Are you sure you want to create invoices?`, this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const createResult = await this.utility.createInvoices(selectedRows.map((x) => x.id));
        console.log('create', createResult);
        if (!createResult.success) {
            this.appInfo.pleaseWaitHide();
            await alert(createResult.message, 'Invoices not created');
            return;
        }

        await this.getData();
        this.appInfo.pleaseWaitHide();
        await alert('Invoices created', this.appInfo.settings.company);
    }

    async delete() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;
        if (!(await confirm(`Are you sure you want to delete the selected ${selectedRows.length === 1 ? 'row' : 'rows'}?`, this.appInfo.settings.company))) return;
        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            row.deleted = new Date();
            row.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async toggleOnHold(loads: Load[]) {
        if (!loads || loads.length === 0) return;

        this.appInfo.pleaseWaitShow();
        const clearOnHold = loads.every((l) => l.onHold);
        loads.forEach((load) => {
            if (clearOnHold) load.onHold = null;
            else if (!load.onHold) load.onHold = new Date();
        });
        await this.uow.commit();
        this.dataGrid.instance.refresh();
        this.appInfo.pleaseWaitHide();
    }

    async getAttachments() {
        this.attachments = await this.uow.getRecentTicketAttachments(6);
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
        this.refreshTimeout = setInterval(() => this.getLoads(), 2000);
    }

    async uploaded(e) {
        if (e && e.request && e.request.responseText) {
            const response = JSON.parse(e.request.responseText) as { success: boolean; message?: string };
            if (!response.success) {
                await alert(response.message ?? 'An error occurred while attempting to upload the file(s)', 'Upload Error');
            } else {
                await this.getAttachments();
            }
        }
        clearInterval(this.refreshTimeout);
        alert('File uploaded', 'File Upload Success');
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
        clearInterval(this.refreshTimeout);
    }

    openAttachment(attachment: TicketAttachment) {
        if (!attachment) return;
        window.open(attachment.filename, '_blank');
    }

    async removeAttachment(attachment: TicketAttachment) {
        if (!attachment) return;

        const success = await confirm('Are you sure you want to delete this ticket?', this.appInfo.settings.company);
        if (!success) return;

        await this.appInfo.pleaseWaitShow();
        attachment.deleted = new Date();
        await this.uow.commit();
        await this.getAttachments();
        await this.appInfo.pleaseWaitHide();
    }

    async assignCustomer(loads: Load[]) {
        if (!loads || loads.length === 0) return;

        const hasCustomer = loads.some((l) => l.customerId);
        if (hasCustomer) {
            alert('Cannot assign a customer to a load with an existing customer', this.appInfo.settings.company);
            return;
        }

        this.appInfo.pleaseWaitShow();
        const customers = await this.uow.customers.whereActive();
        this.appInfo.pleaseWaitHide();

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Assign Customer',
                mode: 'selectWithId',
                displayExpr: 'codeDisplayName',
                dialogValueArray: _(customers)
                    .orderBy((c) => c.codeDisplayName)
                    .valueOf(),
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result && result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                loads.forEach((load) => {
                    const customer = customers.find((c) => c.id === result.value);
                    load.customerId = customer.id;
                    load.customerString = customer.customerUnixKey;
                });
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async assignProduct(loads: Load[]) {
        if (!loads || loads.length === 0) return;

        const hasProducts = loads.some((l) => l.productId);
        if (hasProducts) {
            alert('Cannot assign a product to a load with an existing product', this.appInfo.settings.company);
            return;
        }

        this.appInfo.pleaseWaitShow();
        const products = await this.uow.products.whereActive('partNumber');
        this.appInfo.pleaseWaitHide();

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Assign Product',
                mode: 'selectWithId',
                displayExpr: 'partNumber',
                dialogValueArray: products,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result && result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                loads.forEach((load) => {
                    const product = products.find((p) => p.id === result.value);
                    load.productId = product.id;
                    load.productString = product.partNumber;
                });
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async createQuote(load: Load) {
        if (!load) return;

        await this.appInfo.pleaseWaitShow();
        const response = await this.quoteService.createQuoteFromLoad(load.id);
        await this.appInfo.pleaseWaitHide();

        if (!response.success) {
            await alert(`Cannot create quote. ${response.message}`, this.appInfo.settings.company);
            return;
        }

        const modalRef = this.modalService.show(QuoteDetailComponent, {
            class: 'modal-full',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                quote: response.value,
                quoteLines: response.value.quoteLines,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result?.success) {
                await alert('Quote created', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    async editAdditives(load: Load) {
        if (!load) return;

        const modalRef = this.modalService.show(LoadAdditiveOverviewDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                loadAdditives: load.loadAdditives,
                loadId: load.id
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.getData();
            }
            modalRef.hide();
        });
    }
}
